import React, { useState, useRef, useEffect } from 'react';
import { FaSearch, FaCamera, FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLoader } from '../../../base/context/LoaderProvider';
import { SearchLogService } from '../../../service/admin/searchLogService';
import { fetchToken } from '../../../lib/globalApiClient';
import './style.css';

const API_URL = process.env.REACT_APP_APIUrl1688;

const InternalUsers = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const loader = useLoader();
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchUrl, setSearchUrl] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [uploadedImage, setUploadedImage] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchType, setSearchType] = useState('keyword');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [categories, setCategories] = useState([]);
  const fileInputRef = useRef(null);

  // Ensure we stay on the main internal users page
  useEffect(() => {
    if (location.pathname === '/admin/internal-users/search') {
      navigate('/admin/internal-users', { replace: true });
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        // Try multiple endpoints
        let response = await fetch(`${process.env.REACT_APP_APIUrl}category/get-all`);
        let data = await response.json();

        if (!data.success) {
          response = await fetch(`${process.env.REACT_APP_APIUrl}categories`);
          data = await response.json();
        }

        console.log('Categories API Response:', data);

        let categoryList = [];

        if (data.success && data.data && Array.isArray(data.data)) {
          categoryList = data.data;
        } else if (data.categories && Array.isArray(data.categories)) {
          categoryList = data.categories;
        } else if (Array.isArray(data)) {
          categoryList = data;
        }

        if (categoryList.length > 0) {
          const categoryNames = categoryList.map(cat => {
            if (typeof cat === 'string') return cat;
            return cat.name || cat.categoryName || cat.title || cat.category || cat._id;
          }).filter(name => name);

          console.log('Extracted category names:', categoryNames);
          setCategories(categoryNames);
        } else {
          console.log('Using fallback categories');
          setCategories(['Electronics', 'Fashion', 'Home & Garden', 'Sports', 'Beauty', 'Automotive', 'Books', 'Toys', 'Health', 'Industrial', 'Agriculture', 'Construction', 'Machinery', 'Textiles', 'Chemicals', 'Food & Beverage', 'Packaging', 'Security', 'Transportation', 'Energy', 'Telecommunications', 'Office Supplies']);
        }
      } catch (error) {
        console.error('Failed to fetch categories:', error);
        setCategories(['Electronics', 'Fashion', 'Home & Garden', 'Sports', 'Beauty', 'Automotive', 'Books', 'Toys', 'Health', 'Industrial', 'Agriculture', 'Construction', 'Machinery', 'Textiles', 'Chemicals', 'Food & Beverage', 'Packaging', 'Security', 'Transportation', 'Energy', 'Telecommunications', 'Office Supplies']);
      }
    };
    fetchCategories();
  }, []);

  const searchProducts = async (keyword, page = 1, pageSize = 40) => {
    try {
      const token = await fetchToken();
      const response = await fetch(API_URL + '/get-product-info', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Token': token
        },
        body: JSON.stringify({
          keyword: keyword,
          beginPage: page,
          pageSize: pageSize
        })
      });

      const data = await response.json();

      if (data.result?.success && data.result?.result?.data) {
        const products = data.result.result.data.map(item => ({
          id: item.offerId,
          title: item.subjectTrans || item.subject,
          image: item.imageUrl,
          price: {
            rmb: parseFloat(item.priceInfo.price),
            inr: parseFloat(item.priceInfo.price) * 12.5
          },
          category: 'Product',
          supplier: item.detailed_info?.productShippingInfo?.sendGoodsAddressText || 'Supplier',
          monthSold: item.monthSold,
          tradeScore: item.tradeScore,
          promotionURL: item.promotionURL,
          moq: item.priceInfo?.minOrderQuantity || item.minOrderQuantity || 1,
          variants: item.skuProps?.length || 0,
          rating: item.sellerRating || item.rating || (Math.random() * 2 + 3).toFixed(1),
          responseRate: item.responseRate || '98%',
          verified: item.verified || true
        }));
        setHasMore(products.length === pageSize);
        setTotalPages(data.result.result.totalPage || 1);
        return products;
      }
      setHasMore(false);
      return [];
    } catch (error) {
      console.error('Search API error:', error);
      setHasMore(false);
      return [];
    }
  };

  const handleSearch = async (page = 1) => {
    if (!searchKeyword.trim()) return;

    setLoading(true);
    try {
      const results = await searchProducts(searchKeyword, page);
      setProducts(results);
      setCurrentPage(page);
    } catch (error) {
      console.error('Search failed:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageSearch = async (page = 1) => {
    if (!uploadedImage) return;
    setLoading(true);
    try {
      const token = await fetchToken();
      const response = await fetch(API_URL + '/get-product-info-by-image', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Token': token
        },
        body: JSON.stringify({
          imageBase64: uploadedImage,
          beginPage: page,
          pageSize: 40,
          country: 'en'
        })
      });

      const data = await response.json();

      if (data.status === 'success' && data.products) {
        // Save image search to search log
        if (data.imageId) {
          const imageKeyword = `img:${data.imageId}`;
          SearchLogService.saveSearchKeyword(imageKeyword, 'global', 'image-search').catch(err => {
            console.warn('Failed to save image search keyword:', err);
          });
        } else {
          // If no imageId, use timestamp as identifier
          const imageHash = `img:${Date.now()}`;
          SearchLogService.saveSearchKeyword(imageHash, 'global', 'image-search').catch(err => {
            console.warn('Failed to save image search keyword:', err);
          });
        }

        const results = data.products.map(item => ({
          id: item.offerId,
          title: item.subjectTrans || item.subject,
          image: item.imageUrl,
          price: {
            rmb: parseFloat(item.priceInfo.price),
            inr: parseFloat(item.priceInfo.price) * 12.5
          },
          category: 'Product',
          supplier: item.detailed_info?.productShippingInfo?.sendGoodsAddressText || 'Supplier',
          monthSold: item.monthSold,
          tradeScore: item.tradeScore,
          promotionURL: item.promotionURL
        }));
        setProducts(results);
        setCurrentPage(page);
        setHasMore(results.length === 40);
      } else {
        setProducts([]);
      }
    } catch (error) {
      console.error('Image search failed:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const clearImage = () => {
    setUploadedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const extractOfferIdFromUrl = (url) => {
    try {
      const urlObj = new URL(url);
      const params = new URLSearchParams(urlObj.search);
      const paramOfferId = params.get('offer') || params.get('offerId');
      if (paramOfferId) return paramOfferId;

      const pathMatch = urlObj.pathname.match(/\/offer\/([0-9]+)/);
      return pathMatch ? pathMatch[1] : null;
    } catch {
      const offerMatch = url.match(/\/offer\/([0-9]+)|offer[=:]([0-9]+)/);
      return offerMatch ? (offerMatch[1] || offerMatch[2]) : null;
    }
  };

  const searchByUrl = async () => {
    const offerId = extractOfferIdFromUrl(searchUrl);
    if (!offerId) {
      alert('Could not extract offer ID from URL');
      return;
    }

    setLoading(true);
    try {
      const token = await fetchToken();
      const response = await fetch(`${API_URL}/product-detail-by-offerid`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Token': token
        },
        body: JSON.stringify({
          offerId: offerId,
          country: 'en'
        })
      });

      const data = await response.json();

      if (data.result?.success && data.result?.result) {
        const product = data.result.result;
        const result = [{
          id: product.offerId || offerId,
          title: product.subjectTrans || product.subject,
          image: product.productImage?.images?.[0] || product.imageUrl,
          price: {
            rmb: parseFloat(product.productSaleInfo?.priceRangeList?.[0]?.price || 0),
            inr: parseFloat(product.productSaleInfo?.priceRangeList?.[0]?.price || 0) * 12.5
          },
          category: 'Product',
          supplier: product.companyName || 'Supplier',
          monthSold: product.monthSold,
          tradeScore: product.tradeScore
        }];
        setProducts(result);
        setCurrentPage(1);
        setHasMore(false);
      } else {
        setProducts([]);
        alert('Product not found');
      }
    } catch (error) {
      console.error('URL search failed:', error);
      setProducts([]);
      alert('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const searchByCategory = async (page = 1) => {
    if (!selectedCategory) return;

    setLoading(true);
    try {
      const token = await fetchToken();
      const response = await fetch(API_URL + '/product-by-keyword', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Token': token
        },
        body: JSON.stringify({
          keyword: selectedCategory,
          beginPage: page,
          pageSize: 40,
          country: 'en'
        })
      });

      const data = await response.json();

      if (data.result?.success && data.result?.result?.data) {
        const products = data.result.result.data.map(item => ({
          id: item.offerId,
          title: item.subjectTrans || item.subject,
          image: item.imageUrl,
          price: {
            rmb: parseFloat(item.priceInfo.price),
            inr: parseFloat(item.priceInfo.price) * 12.5
          },
          category: 'Product',
          supplier: item.detailed_info?.productShippingInfo?.sendGoodsAddressText || 'Supplier',
          monthSold: item.monthSold,
          tradeScore: item.tradeScore
        }));
        setProducts(products);
        setCurrentPage(page);
        setHasMore(products.length === 40);
        setTotalPages(data.result.result.totalPage || 1);
      } else {
        setProducts([]);
        setHasMore(false);
      }
    } catch (error) {
      console.error('Category search failed:', error);
      setProducts([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchAction = () => {
    setCurrentPage(1);
    if (searchType === 'image' && uploadedImage) {
      handleImageSearch(1);
    } else if (searchType === 'url' && searchUrl.trim()) {
      searchByUrl();
    } else if (searchType === 'category' && selectedCategory) {
      searchByCategory(1);
    } else if (searchType === 'keyword' && searchKeyword.trim()) {
      handleSearch(1);
    }
  };

  const handlePageChange = (newPage) => {
    if (searchType === 'image' && uploadedImage) {
      handleImageSearch(newPage);
    } else if (searchType === 'category' && selectedCategory) {
      searchByCategory(newPage);
    } else if (searchType === 'keyword' && searchKeyword.trim()) {
      handleSearch(newPage);
    }
  };



  return (
    <div className="internal-users-container">
      <div className="search-header">

        <div className="search-controls">
          <select
            value={searchType}
            onChange={(e) => setSearchType(e.target.value)}
            className="search-type-select"
          >
            <option value="keyword">Keyword</option>
            <option value="url">URL</option>
            <option value="category">Category</option>
            <option value="image">Image</option>
          </select>

          {searchType === 'keyword' && (
            <div className="search-input-group">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search by keyword..."
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearchAction()}
                className="internal-user-search-input"
              />
            </div>
          )}

          {searchType === 'url' && (
            <div className="search-input-group">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Enter 1688.com product URL..."
                value={searchUrl}
                onChange={(e) => setSearchUrl(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearchAction()}
                className="search-input"
              />
            </div>
          )}

          {searchType === 'category' && (
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="category-select-main"
            >
              <option value="">Select Category</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          )}

          {searchType === 'image' && (
            <div className="upload-section">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                accept="image/*"
                style={{ display: 'none' }}
              />
              <button
                onClick={() => fileInputRef.current.click()}
                className="upload-btn"
              >
                <FaCamera />
              </button>
              {uploadedImage && (
                <div className="uploaded-preview">
                  <img src={uploadedImage} alt="Search" referrerPolicy="no-referrer" />
                  <button onClick={clearImage} className="clear-image-btn">
                    <FaTimes />
                  </button>
                </div>
              )}
            </div>
          )}

          <button onClick={handleSearchAction} className="internal-user-search-btn">
            Search
          </button>
        </div>
      </div>

      {loading && (
        <div className="loading">Searching...</div>
      )}

      {!loading && products.length > 0 && (
        <>
          <div className="products-grid">
            {products.map(product => (
              <div
                key={product.id}
                className="product-card-advanced"
                onClick={() => window.open('/admin/internal-users/product-detail?id=' + product.id, '_blank')}
                style={{ cursor: 'pointer' }}
              >
                <div className="product-image-container">
                  <img src={product.image} alt={product.title} className="product-image" referrerPolicy="no-referrer" />
                  {product.verified && <div className="verified-badge">✓</div>}
                  {product.variants > 0 && <div className="variants-badge">{product.variants} variants</div>}
                </div>
                <div className="product-content">
                  <h4 className="product-title">{product.title}</h4>

                  <div className="price-section">
                    <div className="price-main">
                      <span className="price-rmb">¥{product.price.rmb.toFixed(2)}</span>
                      <span className="price-inr">₹{product.price.inr.toFixed(2)}</span>
                    </div>
                    <div className="moq-info">MOQ: {product.moq} pcs</div>
                  </div>

                  <div className="supplier-section">
                    <div className="supplier-info">
                      <span className="supplier-name">{product.supplier}</span>
                      <div className="supplier-stats">
                        <span className="rating">★ {product.rating}</span>
                        <span className="response-rate">{product.responseRate}</span>
                      </div>
                    </div>
                  </div>

                  <div className="sales-metrics">
                    {product.monthSold && (
                      <span className="sales-count">{product.monthSold} sold</span>
                    )}
                    {product.tradeScore && (
                      <span className="trade-score">Score: {product.tradeScore}</span>
                    )}
                  </div>

                  <div className="product-actions">
                    <button
                      className="btn-primary"
                      onClick={() => window.open('/admin/internal-users/product-detail?id=' + product.id, '_blank')}
                    >
                      View Details
                    </button>
                    <button
                      className="btn-secondary"
                      onClick={() => window.open('/admin/internal-users/find-similar?id=' + product.id, '_blank')}
                    >
                      Find Similar
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="pagination">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="page-btn"
            >
              <FaChevronLeft /> Prev
            </button>

            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                >
                  {pageNum}
                </button>
              );
            })}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={!hasMore || currentPage >= totalPages}
              className="page-btn"
            >
              Next <FaChevronRight />
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default InternalUsers;